import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import usePlanFeatures from '../hooks/usePlanFeatures';

// Maps a feature key → the minimum plan tier that unlocks it. Kept in
// sync with /app/backend/utils/plan_features.py — if you change the
// plan map there, mirror it here so the upsell copy stays honest.
const FEATURE_MIN_PLAN = {
  profit_calc:      'Core',
  schedule:         'Core',
  documents:        'Plus',
  bookkeeping:      'Plus',
  ai:               'Plus',
  analytics:        'Starter',
  time_tracking:    'Grow',
  payroll:          'Grow',
  advanced_reports: 'Scale',
  ai_unlimited:     'Scale',
  sms_messaging:    'Pro',
  workorders:       'Pro',
};

const FEATURE_LABEL = {
  profit_calc:      'Profit Calculator',
  schedule:         'Scheduling & Calendar',
  documents:        'Files & Contracts',
  bookkeeping:      'Bookkeeping',
  ai:               'Ask Mantle AI',
  analytics:        'Analytics',
  time_tracking:    'Team Time-Tracking',
  payroll:          'Payroll',
  advanced_reports: 'Advanced Reporting & Job Costing',
  ai_unlimited:     'Unlimited Ask Mantle',
  sms_messaging:    'SMS Messaging',
  workorders:       'Workorders & Field Dispatch',
};

const FEATURE_PITCH = {
  profit_calc:      'See live cost-vs-revenue on every estimate and invoice. Stop guessing whether a job actually made you money.',
  schedule:         'Drag-and-drop jobs onto a calendar, send appointment reminders, and never double-book again.',
  documents:        'Upload contracts, e-sign agreements, and keep every client document in one searchable vault.',
  bookkeeping:      'Track every expense, attach receipts, and export a clean Profit & Loss for your CPA in one tap.',
  ai:               'Ask Mantle drafts invoices, creates contracts, schedules jobs, and answers your business questions in plain English.',
  analytics:        'See revenue trends, client lifetime value, and which jobs actually pay — without a spreadsheet.',
  time_tracking:    'Your crew clocks in and out from their phones. Per-job timesheets feed straight into payroll.',
  payroll:          'Pay subcontractors and hourly crew with bulk runs, downloadable pay stubs, and 1099 prep.',
  advanced_reports: 'Job-cost reports, gross-margin per client, and cost-vs-revenue dashboards that show where the money really goes.',
  ai_unlimited:     'Remove the 100/month action cap. Use Ask Mantle as much as you want — drafts, replies, scheduling, the works.',
  sms_messaging:    'Send appointment reminders, payment links, and dispatch jobs to your crew via SMS instead of email.',
  workorders:       'Dispatch jobs to field techs by phone link — they accept, complete, and upload photos from the road.',
};

/**
 * Route-level upgrade screen. Renders children when the workspace plan
 * includes `feature`; otherwise blocks the route with a clean upsell that
 * tells the user EXACTLY which plan unlocks it. Backend `plan_gate.py`
 * middleware enforces the same gate server-side, so this is purely UX.
 */
export default function PlanGate({ feature, children }) {
  const { has, loading } = usePlanFeatures();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#B98A2C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (has(feature)) return children;

  const minPlan = FEATURE_MIN_PLAN[feature] || 'a higher tier';
  const label = FEATURE_LABEL[feature] || 'this feature';
  const pitch = FEATURE_PITCH[feature] || '';

  return (
    <div data-testid={`plan-gate-${feature}`} className="min-h-[70vh] flex items-center justify-center px-4 py-10 animate-fade-in">
      <div className="relative max-w-xl w-full">
        {/* Soft gold halo */}
        <div className="absolute -inset-6 bg-[#B98A2C]/10 blur-3xl rounded-full pointer-events-none" />
        <div className="relative bg-white dark:bg-gradient-to-br dark:from-[#1B2838] dark:to-[#142030] border border-slate-200 dark:border-[#B98A2C]/20 rounded-2xl p-8 shadow-xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#CBA33B] to-[#B98A2C] flex items-center justify-center shadow-md">
              <Lock size={22} className="text-white" strokeWidth={2.4} />
            </div>
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#B98A2C]">{minPlan} plan</p>
              <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {label}
              </h2>
            </div>
          </div>

          <p className="text-sm text-slate-600 dark:text-[#A8B8CA] leading-relaxed mb-6">
            {pitch}
          </p>

          <div className="flex items-start gap-2.5 p-4 mb-6 rounded-xl bg-[#B98A2C]/8 dark:bg-[#B98A2C]/10 border border-[#B98A2C]/20">
            <Sparkles size={16} className="text-[#CBA33B] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-700 dark:text-[#D6E0EC] leading-relaxed">
              Available on <span className="font-semibold text-[#B98A2C]">{minPlan}</span> and above.
              Upgrade now and start using {label} immediately — no data migration, your team and clients carry over.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              data-testid={`plan-gate-upgrade-${feature}`}
              onClick={() => navigate('/settings?section=billing')}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#CBA33B] to-[#B98A2C] hover:from-[#D6B04F] hover:to-[#C99A35] text-white font-semibold text-sm shadow-md transition-all active:scale-[0.98]"
            >
              Upgrade to {minPlan}
              <ArrowRight size={16} />
            </button>
            <button
              data-testid={`plan-gate-back-${feature}`}
              onClick={() => navigate('/app')}
              className="px-5 py-3 rounded-lg border border-slate-200 dark:border-white/10 text-slate-700 dark:text-[#A8B8CA] hover:bg-slate-50 dark:hover:bg-white/[0.03] font-medium text-sm transition-all"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
